import { ImageTuningValues } from 'business/models/ImageTuningModel';
import {
  CaptureResultTuningValuesDTO,
  ExperimentTuningValuesDTO,
} from 'business/models/Models';
import WellService from 'business/services/WellService';
import { GetCaptureProfileTuningValues, isNullOrUndefined } from 'utils/Global';
import { useRockFetchPost } from 'utils/RockFetch';
import { get_tuned_images_data_values as GetTunedImagesDataValues } from '../../../wasm/tuning/image_tuner';

export function imageLeveling(
  imgEl,
  lowerLevelLimit,
  upperLevelLimit,
  mimeType?,
  quality?,
  onTuningCompletion?,
  tuningValues?,
) {
  if (imgEl == undefined) {
    return undefined;
  }

  const defaultRGB = {
      r: 0,
      g: 0,
      b: 0,
    },
    canvas = document.createElement('canvas'),
    context = canvas.getContext && canvas.getContext('2d');

  const newIntensityLevels = [].fill(0);
  const newLowerLevelLimit = 0;
  const newUpperLevelLimit = 255;
  const bytesPerPixel = 4;
  let newValue = 0,
    height = 0,
    width = 0,
    data;

  const scaleFactor =
    (newUpperLevelLimit - newLowerLevelLimit) / (upperLevelLimit - lowerLevelLimit);

  for (let i = 0; i < 256; i++) {
    newValue = (i - lowerLevelLimit) * scaleFactor + newLowerLevelLimit;

    if (newValue < 0) {
      newValue = 0;
    } else if (newValue > 255) {
      newValue = 255;
    }

    newIntensityLevels[i] = newValue;
  }

  if (!context) {
    return defaultRGB;
  }

  height = canvas.height = imgEl.naturalHeight || imgEl.offsetHeight || imgEl.height;
  width = canvas.width = imgEl.naturalWidth || imgEl.offsetWidth || imgEl.width;

  context.drawImage(imgEl, 0, 0);

  try {
    data = context.getImageData(0, 0, width, height);
  } catch (e) {
    console.error(e);
    return defaultRGB;
  }

  const intensityArray = new Uint8Array(newIntensityLevels);
  GetTunedImagesDataValues(data.data, intensityArray, bytesPerPixel);

  const newCanvas = document.createElement('canvas');
  const newContext = newCanvas.getContext('2d');
  newCanvas.width = data.width;
  newCanvas.height = data.height;
  newContext.putImageData(data, 0, 0);

  const resultImage = new Image();
  if (onTuningCompletion) {
    newCanvas.toBlob(
      (blob) => {
        resultImage.src = URL.createObjectURL(blob);
        onTuningCompletion(resultImage, tuningValues, blob?.size);
      },
      mimeType,
      quality,
    );
  } else {
    resultImage.src = newCanvas.toDataURL(mimeType, quality);
    return resultImage;
  }
}

export function histogramSmoothing(histogramValues) {
  const newHistogram = new Array(256).fill(0);

  for (let i = 0; i < 256; i++) {
    newHistogram[i] = histogramValues[i];

    if (i > 0 && i < 255) {
      const prevVal = histogramValues[i - 1];
      const curVal = histogramValues[i];
      const nextVal = histogramValues[i + 1];

      if (curVal < prevVal && curVal < nextVal) {
        newHistogram[i] = (prevVal + nextVal) / 2;
      }
    }
  }

  return newHistogram;
}

export function GetHistogramFromCompositeImage(imgEl) {
  if (imgEl == undefined) {
    return undefined;
  }

  const defaultRGB = {
    r: 0,
    g: 0,
    b: 0,
  }; // for non-supporting envs
  const canvas = document.createElement('canvas');
  const context = canvas.getContext && canvas.getContext('2d');
  let data,
    i = -4,
    avgColor = 0; //ARGB;
  const rgb = {
    a: 0,
    r: 0,
    g: 0,
    b: 0,
  };
  const bytesPerPixel = 4;
  const histogram = new Array(256).fill(0);

  if (!context) {
    return defaultRGB;
  }

  const height = (canvas.height =
    imgEl.naturalHeight || imgEl.offsetHeight || imgEl.height);
  const width = (canvas.width = imgEl.naturalWidth || imgEl.offsetWidth || imgEl.width);

  context.drawImage(imgEl, 0, 0);

  try {
    data = context.getImageData(0, 0, width, height);
  } catch (e) {
    console.error(e);
    return defaultRGB;
  }

  const length = data.data.length;

  while ((i += bytesPerPixel) < length) {
    rgb.r = data.data[i];
    rgb.g = data.data[i + 1];
    rgb.b = data.data[i + 2];
    rgb.a = data.data[i + 3];

    if (rgb.a > 0) {
      avgColor = ~~((rgb.r + rgb.g + rgb.b) / 3); //~~ is for floor function
      histogram[avgColor]++;
    }
  }
  return histogram;
}

export function GetHistogramFromBitmap(imgEl) {
  if (imgEl == undefined) {
    return undefined;
  }

  let data,
    i = -4,
    avgColor = 0; //ARGB
  const rgb = {
    r: 0,
    g: 0,
    b: 0,
  };
  const defaultRGB = {
    r: 0,
    g: 0,
    b: 0,
  };
  const canvas = document.createElement('canvas');
  const context = canvas.getContext && canvas.getContext('2d');
  const bytesPerPixel = 4;
  const histogram = new Array(256).fill(0);

  if (!context) {
    return defaultRGB;
  }

  const height = (canvas.height =
    imgEl.naturalHeight || imgEl.offsetHeight || imgEl.height);
  const width = (canvas.width = imgEl.naturalWidth || imgEl.offsetWidth || imgEl.width);

  context.drawImage(imgEl, 0, 0);

  try {
    data = context.getImageData(0, 0, width, height);
  } catch (e) {
    console.error(e);
    return defaultRGB;
  }

  const length = data.data.length;

  while ((i += bytesPerPixel) < length) {
    rgb.r = data.data[i];
    rgb.g = data.data[i + 1];
    rgb.b = data.data[i + 2];
    avgColor = ~~((rgb.r + rgb.g + rgb.b) / 3); //~~ is for floor function

    if (histogram[avgColor] == undefined) {
      histogram[avgColor] = 0;
    }

    histogram[avgColor]++;
  }

  return histogram;
}

//For Auto Tuning
export function calculateLevelingWindow(
  histogram,
  lowThresholdPercentage,
  highThresholdPercentage,
) {
  let totalPixelsCount = 0;
  for (let i = 0; i < 256; i++) {
    totalPixelsCount += histogram[i];
  }

  let lowerLevelLimit = 0,
    upperLevelLimit = 0;
  let minIntensity = 0,
    maxIntensity = 255;

  for (let i = 0; i <= 255; i++) {
    if (histogram[i] > 0) {
      minIntensity = i;
      break;
    }
  }

  for (let i = 255; i >= 0; i--) {
    if (histogram[i] > 0) {
      maxIntensity = i;
      break;
    }
  }

  const countLowThreashold = (totalPixelsCount * lowThresholdPercentage) / 100;
  let countPixels = 0;
  for (let i = minIntensity; i < maxIntensity; i++) {
    countPixels += histogram[i];
    if (countPixels >= countLowThreashold) {
      lowerLevelLimit = i;
      break;
    }
  }

  const countHighThreshold = (totalPixelsCount * highThresholdPercentage) / 100;
  countPixels = 0;
  for (let i = maxIntensity; i >= minIntensity; i--) {
    countPixels += histogram[i];
    if (countPixels >= countHighThreshold) {
      upperLevelLimit = i;
      break;
    }
  }

  if (upperLevelLimit <= lowerLevelLimit) {
    if (lowerLevelLimit == 255) {
      lowerLevelLimit--;
    }
    upperLevelLimit = lowerLevelLimit + 1;
  }

  return [lowerLevelLimit, upperLevelLimit];
}

export default function getTunedImage(
  image,
  lowerLevelLimit,
  upperLevelLimit,
  mimeType = 'image/jpeg',
  quality = 0.5,
  onTuningCompletion?,
  tuningValues?,
) {
  const tunedImage = imageLeveling(
    image,
    lowerLevelLimit,
    upperLevelLimit,
    mimeType,
    quality,
    onTuningCompletion,
    tuningValues,
  );
  return tunedImage;
}

export function getDefaultTuningWindow(captureProfileId) {
  const captureProfileTuningValues = GetCaptureProfileTuningValues();
  if (captureProfileTuningValues != undefined && captureProfileTuningValues.length > 0) {
    const cpTuningValues = captureProfileTuningValues.filter(
      (tv) => tv.captureProfileID == captureProfileId,
    );
    if (cpTuningValues != undefined && cpTuningValues.length !== 0) {
      const tuningValuesVM = {
        lowerLevelLimit: cpTuningValues[0].lowerLevelLimit,
        upperLevelLimit: cpTuningValues[0].upperLevelLimit,
        isAutoLevelingOn: cpTuningValues[0].isAutoLevelingOn,
      };

      return tuningValuesVM;
    }
  }

  return undefined;
}

export function getTuningWindowForPlateAuto(
  experimentTuningValues,
  orgImage,
  captureProfileID,
  isAutoMode,
) {
  const cpId = captureProfileID;
  const imageTuningVM: ImageTuningValues = {};

  if (cpId === undefined || orgImage == undefined) {
    return undefined;
  }

  let histogramValues;
  const tuningValues = experimentTuningValues.filter((tv) => tv.captureProfileID == cpId);
  if (tuningValues != undefined && tuningValues.length !== 0) {
    if (isAutoMode) {
      histogramValues = GetHistogramFromCompositeImage(orgImage);
      histogramValues = histogramSmoothing(histogramValues);
      if (histogramValues != null) {
        const window = calculateLevelingWindow(
          histogramValues,
          tuningValues[0].autoLevelingLowThreshold,
          tuningValues[0].autoLevelingHighThreshold,
        );
        if (window != undefined) {
          imageTuningVM.autoLevelingLowerLimit = window[0];
          imageTuningVM.autoLevelingUpperLimit = window[1];
          imageTuningVM.lowerLevelLimit = window[0];
          imageTuningVM.upperLevelLimit = window[1];
          imageTuningVM.isAutoLevelingOn = isAutoMode;
        }
      }
    } else {
      imageTuningVM.lowerLevelLimit = tuningValues[0].lowerLevelLimit;
      imageTuningVM.upperLevelLimit = tuningValues[0].upperLevelLimit;
      imageTuningVM.isAutoLevelingOn = isAutoMode;
    }
  } else {
    const captureProfileTuningValues = GetCaptureProfileTuningValues();
    if (
      captureProfileTuningValues != undefined &&
      captureProfileTuningValues.length > 0
    ) {
      const cpTuningValues = captureProfileTuningValues.filter(
        (tv) => tv.captureProfileID == cpId,
      );
      if (cpTuningValues != undefined && cpTuningValues.length !== 0) {
        if (isAutoMode) {
          histogramValues = GetHistogramFromCompositeImage(orgImage);
          histogramValues = histogramSmoothing(histogramValues);
          if (histogramValues != null) {
            const window = calculateLevelingWindow(
              histogramValues,
              cpTuningValues[0].autoLevelingLowThreshold,
              cpTuningValues[0].autoLevelingHighThreshold,
            );
            if (window != undefined) {
              imageTuningVM.autoLevelingLowerLimit = window[0];
              imageTuningVM.autoLevelingUpperLimit = window[1];
              imageTuningVM.lowerLevelLimit = window[0];
              imageTuningVM.upperLevelLimit = window[1];
              imageTuningVM.isAutoLevelingOn = isAutoMode;
            }
          }
        } else {
          imageTuningVM.lowerLevelLimit = cpTuningValues[0].lowerLevelLimit;
          imageTuningVM.upperLevelLimit = cpTuningValues[0].upperLevelLimit;
        }
      }
    } else {
      return undefined;
    }
  }

  return imageTuningVM;
}

export function getTuningWindowForPlate(
  experimentTuningValues: ExperimentTuningValuesDTO[],
  orgImage,
  captureProfileID,
  calculateAuto = true,
) {
  const cpId = captureProfileID;
  const imageTuningVM: ImageTuningValues = {};

  if (cpId === undefined || orgImage == undefined) {
    return undefined;
  }

  let histogramValues;
  if (experimentTuningValues != undefined && experimentTuningValues.length !== 0) {
    if (experimentTuningValues[0].isAutoLevelingOn && calculateAuto) {
      histogramValues = GetHistogramFromCompositeImage(orgImage);
      histogramValues = histogramSmoothing(histogramValues);
      if (histogramValues != null) {
        const window = calculateLevelingWindow(
          histogramValues,
          experimentTuningValues[0].autoLevelingLowThreshold,
          experimentTuningValues[0].autoLevelingHighThreshold,
        );
        if (window != undefined) {
          imageTuningVM.autoLevelingLowerLimit = window[0];
          imageTuningVM.autoLevelingUpperLimit = window[1];
          imageTuningVM.lowerLevelLimit = window[0];
          imageTuningVM.upperLevelLimit = window[1];
          imageTuningVM.isAutoLevelingOn = experimentTuningValues[0].isAutoLevelingOn;
        }
      }
    } else {
      imageTuningVM.lowerLevelLimit = experimentTuningValues[0].lowerLevelLimit;
      imageTuningVM.upperLevelLimit = experimentTuningValues[0].upperLevelLimit;
      imageTuningVM.isAutoLevelingOn = experimentTuningValues[0].isAutoLevelingOn;
    }
  } else {
    const captureProfileTuningValues = GetCaptureProfileTuningValues();
    if (
      captureProfileTuningValues != undefined &&
      captureProfileTuningValues.length > 0
    ) {
      const cpTuningValues = captureProfileTuningValues.filter(
        (tv) => tv.captureProfileID == cpId,
      );
      if (cpTuningValues != undefined && cpTuningValues.length !== 0) {
        if (cpTuningValues[0].isAutoLevelingOn) {
          histogramValues = GetHistogramFromCompositeImage(orgImage);
          histogramValues = histogramSmoothing(histogramValues);
          if (histogramValues != null) {
            const window = calculateLevelingWindow(
              histogramValues,
              cpTuningValues[0].autoLevelingLowThreshold,
              cpTuningValues[0].autoLevelingHighThreshold,
            );
            if (window != undefined) {
              imageTuningVM.autoLevelingLowerLimit = window[0];
              imageTuningVM.autoLevelingUpperLimit = window[1];
              imageTuningVM.lowerLevelLimit = window[0];
              imageTuningVM.upperLevelLimit = window[1];
              imageTuningVM.isAutoLevelingOn = cpTuningValues[0].isAutoLevelingOn;
            }
          }
        } else {
          imageTuningVM.lowerLevelLimit = cpTuningValues[0].lowerLevelLimit;
          imageTuningVM.upperLevelLimit = cpTuningValues[0].upperLevelLimit;
          imageTuningVM.isAutoLevelingOn = cpTuningValues[0].isAutoLevelingOn;
        }
      }
    } else {
      return undefined;
    }
  }

  return imageTuningVM;
}

export function getTunedImageForPlate(
  tuningValues,
  orgImage,
  captureProfileID,
  isAutoLevelingOn,
) {
  let imageTuningVM;
  if (isAutoLevelingOn) {
    imageTuningVM = getTuningWindowForPlateAuto(
      tuningValues,
      orgImage,
      captureProfileID,
      isAutoLevelingOn,
    );
  } else {
    imageTuningVM = getTuningWindowForPlate(tuningValues, orgImage, captureProfileID);
  }

  let lowerLevelLimit, upperLevelLimit;

  if (imageTuningVM === undefined) {
    return undefined;
  }

  if (imageTuningVM.isAutoLevelingOn == true) {
    lowerLevelLimit = imageTuningVM.autoLevelingLowerLimit;
    upperLevelLimit = imageTuningVM.autoLevelingUpperLimit;
  } else {
    lowerLevelLimit = imageTuningVM.lowerLevelLimit;
    upperLevelLimit = imageTuningVM.upperLevelLimit;
  }

  if (lowerLevelLimit == undefined || upperLevelLimit == undefined) {
    return undefined;
  }

  const tunedImage = getTunedImage(
    orgImage,
    lowerLevelLimit,
    upperLevelLimit,
    'image/png',
    1,
  );
  return tunedImage;
}

export function getExperimentTuningValuesVM(
  experimentId,
  lowerLevelLimit,
  upperLevelLimit,
  captureProfileID,
  isAutoLevelingOn,
) {
  const captureProfileTuningValues = GetCaptureProfileTuningValues();

  if (captureProfileTuningValues === undefined) {
    return undefined;
  }

  const cptuningValues = captureProfileTuningValues.filter(
    (tv) => tv.captureProfileID == captureProfileID,
  );

  if (cptuningValues === undefined || cptuningValues.length === 0) {
    return undefined;
  }
  const tuningValuesVM = {
    experimentId: experimentId,
    captureProfileID: captureProfileID,
    lowerLevelLimit: lowerLevelLimit,
    upperLevelLimit: upperLevelLimit,
    autoLevelingLowThreshold: cptuningValues[0].autoLevelingLowThreshold,
    autoLevelingHighThreshold: cptuningValues[0].autoLevelingHighThreshold,
    isAutoLevelingOn: isAutoLevelingOn,
  };

  return tuningValuesVM;
}

export function updateTuningValuesForPlate(
  experimentTuningValues,
  lowerLevelLimit,
  upperLevelLimit,
  captureProfileID,
  isAutoLevelingOn,
  experimentId,
) {
  let cpTuningValues;
  const captureProfileTuningValues = GetCaptureProfileTuningValues();
  if (captureProfileTuningValues != undefined && captureProfileTuningValues.length > 0) {
    cpTuningValues = captureProfileTuningValues.filter(
      (tv) => tv.captureProfileID == captureProfileID,
    );
  }

  const tuningValuesIndex = experimentTuningValues.findIndex(
    (tv) => tv.captureProfileID == captureProfileID,
  );
  if (tuningValuesIndex < 0) {
    if (cpTuningValues != undefined && cpTuningValues.length !== 0) {
      const tuningValuesVM = {
        experimentId: experimentId,
        captureProfileID: captureProfileID,
        lowerLevelLimit: lowerLevelLimit,
        upperLevelLimit: upperLevelLimit,
        isAutoLevelingOn:
          isAutoLevelingOn == undefined
            ? cpTuningValues[0].isAutoLevelingOn
            : isAutoLevelingOn,
        autoLevelingLowThreshold: cpTuningValues[0].autoLevelingLowThreshold,
        autoLevelingHighThreshold: cpTuningValues[0].autoLevelingHighThreshold,
      };

      return [...experimentTuningValues, tuningValuesVM];
    }
  } else {
    if (!isAutoLevelingOn) {
      experimentTuningValues[tuningValuesIndex].lowerLevelLimit = lowerLevelLimit;
      experimentTuningValues[tuningValuesIndex].upperLevelLimit = upperLevelLimit;
      experimentTuningValues[tuningValuesIndex].isAutoLevelingOn = false;
    } else {
      experimentTuningValues[tuningValuesIndex].isAutoLevelingOn = true;
    }
    return [...experimentTuningValues];
  }
}
const [rockFetchPost] = useRockFetchPost();
export async function saveExperimentTuningValues(
  lowerLimit,
  upperLimit,
  captureProfileID,
  isAutoLevelingOn,
  experimentId,
) {
  const tuningValuesVM = getExperimentTuningValuesVM(
    experimentId,
    lowerLimit,
    upperLimit,
    captureProfileID,
    isAutoLevelingOn,
  );

  if (tuningValuesVM === undefined) {
    return;
  }

  await rockFetchPost('/api/experiment/savetuninginfo', tuningValuesVM);
}

async function deleteExperimentTuningValues(experimentTuningValues, captureProfileId) {
  let result;
  const tuningValues = experimentTuningValues.filter(
    (tv) => tv.captureProfileID == captureProfileId,
  );
  if (tuningValues != undefined && tuningValues.length !== 0) {
    const tuningValuesVM = {
      experimentID: tuningValues[0].experimentId || tuningValues[0].experimentID,
      captureProfileID: tuningValues[0].captureProfileID,
    };

    result = await rockFetchPost('/api/experiment/deletetuninginfo', tuningValuesVM);

    if (result > -1) {
      return result;
    }
  }

  return undefined;
}

export function resetExperimentTuningValues(experimentTuningValues, captureProfileId) {
  const tvId = deleteExperimentTuningValues(experimentTuningValues, captureProfileId);
  if (tvId != undefined) {
    const tuningValues = experimentTuningValues.filter(
      (tv) => tv.captureProfileID == captureProfileId,
    );
    if (tuningValues != undefined && tuningValues.length !== 0) {
      const index = experimentTuningValues.indexOf(tuningValues[0]);
      experimentTuningValues.splice(index, 1);
    }
  }
}

///////Drop level image tuning manager
export function getTuningWindowForDrop(
  tuningValues: ExperimentTuningValuesDTO[],
  orgImage,
  captureProfileID,
  captureResultTuningValuesVM,
  calculateAuto = true,
) {
  let histogramValues;
  let imageTuningVM: ImageTuningValues = {};
  if (!isNullOrUndefined(captureResultTuningValuesVM)) {
    if (captureResultTuningValuesVM.isAutoLevelingOn && calculateAuto) {
      histogramValues = GetHistogramFromCompositeImage(orgImage);
      histogramValues = histogramSmoothing(histogramValues);
      if (histogramValues != null) {
        const window = calculateLevelingWindow(
          histogramValues,
          captureResultTuningValuesVM.autoLevelingLowThreshold,
          captureResultTuningValuesVM.autoLevelingHighThreshold,
        );
        if (window != undefined) {
          imageTuningVM.autoLevelingLowerLimit = window[0];
          imageTuningVM.autoLevelingUpperLimit = window[1];
          imageTuningVM.lowerLevelLimit = window[0];
          imageTuningVM.upperLevelLimit = window[1];
          imageTuningVM.isAutoLevelingOn = captureResultTuningValuesVM.isAutoLevelingOn;
        }
      }
    } else {
      imageTuningVM.lowerLevelLimit = captureResultTuningValuesVM.lowerLevelLimit;
      imageTuningVM.upperLevelLimit = captureResultTuningValuesVM.upperLevelLimit;
    }
  } else {
    imageTuningVM = getTuningWindowForPlate(
      tuningValues,
      orgImage,
      captureProfileID,
      calculateAuto,
    );
  }

  return imageTuningVM;
}

/**
 * These methods are copies for SNF because original method takes whole expData object
 * and uses only single value, but in SNF the expData object is not available
 * The original methods will be updated to take only necessary params instead of whole object after 4.7 release
 * so that testing can be done to make sure there are no ripples
 */

export function getTuningWindowForDropSnf(
  experimentTuningValues,
  orgImage,
  captureProfileID,
  captureResultTuningValuesVM,
  calculateAuto = true,
) {
  let histogramValues;
  let imageTuningVM: ImageTuningValues = {};
  if (!isNullOrUndefined(captureResultTuningValuesVM)) {
    if (captureResultTuningValuesVM.isAutoLevelingOn && calculateAuto) {
      histogramValues = GetHistogramFromCompositeImage(orgImage);
      histogramValues = histogramSmoothing(histogramValues);
      if (histogramValues != null) {
        const window = calculateLevelingWindow(
          histogramValues,
          captureResultTuningValuesVM.autoLevelingLowThreshold,
          captureResultTuningValuesVM.autoLevelingHighThreshold,
        );
        if (window != undefined) {
          imageTuningVM.autoLevelingLowerLimit = window[0];
          imageTuningVM.autoLevelingUpperLimit = window[1];
          imageTuningVM.lowerLevelLimit = window[0];
          imageTuningVM.upperLevelLimit = window[1];
          imageTuningVM.isAutoLevelingOn = captureResultTuningValuesVM.isAutoLevelingOn;
        }
      }
    } else {
      imageTuningVM.lowerLevelLimit = captureResultTuningValuesVM.lowerLevelLimit;
      imageTuningVM.upperLevelLimit = captureResultTuningValuesVM.upperLevelLimit;
    }
  } else {
    imageTuningVM = getTuningWindowForPlateSnf(
      experimentTuningValues,
      orgImage,
      captureProfileID,
      calculateAuto,
    );
  }

  return imageTuningVM;
}
export function getTuningWindowForPlateSnf(
  experimentTuningValues,
  orgImage,
  captureProfileID,
  calculateAuto = true,
) {
  const cpId = captureProfileID;
  const imageTuningVM: ImageTuningValues = {};

  if (cpId === undefined || orgImage == undefined) {
    return undefined;
  }

  let histogramValues;
  const tuningValues = experimentTuningValues.filter((tv) => tv.captureProfileID == cpId);
  if (tuningValues != undefined && tuningValues.length !== 0) {
    if (tuningValues[0].isAutoLevelingOn && calculateAuto) {
      histogramValues = GetHistogramFromCompositeImage(orgImage);
      histogramValues = histogramSmoothing(histogramValues);
      if (histogramValues != null) {
        const window = calculateLevelingWindow(
          histogramValues,
          tuningValues[0].autoLevelingLowThreshold,
          tuningValues[0].autoLevelingHighThreshold,
        );
        if (window != undefined) {
          imageTuningVM.autoLevelingLowerLimit = window[0];
          imageTuningVM.autoLevelingUpperLimit = window[1];
          imageTuningVM.lowerLevelLimit = window[0];
          imageTuningVM.upperLevelLimit = window[1];
          imageTuningVM.isAutoLevelingOn = tuningValues[0].isAutoLevelingOn;
        }
      }
    } else {
      imageTuningVM.lowerLevelLimit = tuningValues[0].lowerLevelLimit;
      imageTuningVM.upperLevelLimit = tuningValues[0].upperLevelLimit;
      imageTuningVM.isAutoLevelingOn = tuningValues[0].isAutoLevelingOn;
    }
  } else {
    const captureProfileTuningValues = GetCaptureProfileTuningValues();
    if (
      captureProfileTuningValues != undefined &&
      captureProfileTuningValues.length > 0
    ) {
      const cpTuningValues = captureProfileTuningValues.filter(
        (tv) => tv.captureProfileID == cpId,
      );
      if (cpTuningValues != undefined && cpTuningValues.length !== 0) {
        if (cpTuningValues[0].isAutoLevelingOn) {
          histogramValues = GetHistogramFromCompositeImage(orgImage);
          histogramValues = histogramSmoothing(histogramValues);
          if (histogramValues != null) {
            const window = calculateLevelingWindow(
              histogramValues,
              cpTuningValues[0].autoLevelingLowThreshold,
              cpTuningValues[0].autoLevelingHighThreshold,
            );
            if (window != undefined) {
              imageTuningVM.autoLevelingLowerLimit = window[0];
              imageTuningVM.autoLevelingUpperLimit = window[1];
              imageTuningVM.lowerLevelLimit = window[0];
              imageTuningVM.upperLevelLimit = window[1];
              imageTuningVM.isAutoLevelingOn = cpTuningValues[0].isAutoLevelingOn;
            }
          }
        } else {
          imageTuningVM.lowerLevelLimit = cpTuningValues[0].lowerLevelLimit;
          imageTuningVM.upperLevelLimit = cpTuningValues[0].upperLevelLimit;
          imageTuningVM.isAutoLevelingOn = cpTuningValues[0].isAutoLevelingOn;
        }
      }
    } else {
      return undefined;
    }
  }

  return imageTuningVM;
}
export function getTunedImageForDrop(
  orgImage,
  isAutoLevelingOn,
  autoLevelingLowerLimit,
  autoLevelingUpperLimit,
  lowerLevelLimit,
  upperLevelLimit,
  onTuningCompletion?,
  tuningValues?,
  quality = 0.5,
) {
  const tunedImage = getTunedImage(
    orgImage,
    lowerLevelLimit,
    upperLevelLimit,
    'image/jpeg',
    quality,
    onTuningCompletion,
    tuningValues,
  );
  return tunedImage;
}

export function getTunedImageForDropByVM(orgImage, tuningWindowVM) {
  return getTunedImageForDrop(
    orgImage,
    tuningWindowVM.isAutoLevelingOn,
    tuningWindowVM.autoLevelingLowerLimit,
    tuningWindowVM.autoLevelingUpperLimit,
    tuningWindowVM.lowerLevelLimit,
    tuningWindowVM.upperLevelLimit,
  );
}

export function getDropTuningValuesVM(
  lowerLevelLimit,
  upperLevelLimit,
  captureProfileId,
  dropRegionId,
  isAutoLevelingOn,
) {
  const captureProfileTuningValues = GetCaptureProfileTuningValues();

  if (captureProfileTuningValues === undefined) {
    return undefined;
  }

  const cptuningValues = captureProfileTuningValues.filter(
    (tv) => tv.captureProfileID == captureProfileId,
  );

  if (cptuningValues === undefined || cptuningValues.length === 0) {
    return undefined;
  }

  const tuningValuesVM: ImageTuningValues = {
    regionId: dropRegionId,
    lowerLevelLimit: lowerLevelLimit,
    upperLevelLimit: upperLevelLimit,
    autoLevelingLowThreshold: cptuningValues[0].autoLevelingLowThreshold,
    autoLevelingHighThreshold: cptuningValues[0].autoLevelingHighThreshold,
    isAutoLevelingOn: isAutoLevelingOn,
  };

  return tuningValuesVM;
}

export async function saveDropTuningValues(
  lowerLimit,
  upperLimit,
  captureProfileId,
  dropRegionId,
  imageBatchId,
  isAutoLevelingOn,
) {
  const tuningValuesVM = getDropTuningValuesVM(
    lowerLimit,
    upperLimit,
    captureProfileId,
    dropRegionId,
    isAutoLevelingOn,
  );

  if (tuningValuesVM === undefined) {
    return;
  }

  const tuninVlauesDto = new CaptureResultTuningValuesDTO();
  tuninVlauesDto.autoLevelingHighThreshold = tuningValuesVM.autoLevelingHighThreshold;
  tuninVlauesDto.autoLevelingLowThreshold = tuningValuesVM.autoLevelingLowThreshold;
  tuninVlauesDto.isAutoLevelingOn = tuningValuesVM.isAutoLevelingOn;
  tuninVlauesDto.lowerLevelLimit = tuningValuesVM.lowerLevelLimit;
  tuninVlauesDto.upperLevelLimit = tuningValuesVM.upperLevelLimit;
  tuninVlauesDto.cpId = captureProfileId;
  tuninVlauesDto.imageBatchId = imageBatchId;
  tuninVlauesDto.regionID = dropRegionId;

  await new WellService().savecaptureresulttuningvalues(tuninVlauesDto);
}

export async function deleteDropTuningValues(
  dropRegionID,
  captureProfileId,
  imageBatchId,
) {
  const tuningValuesVM = {
    regionID: dropRegionID,
    imageBatchId: imageBatchId,
    cpId: captureProfileId,
  };

  const result = await rockFetchPost(
    '/api/wellinfo/deletecaptureresulttuningvalues',
    tuningValuesVM,
  );

  if (result > -1) {
    return result;
  }

  return undefined;
}
